import React from 'react'
import { IonItemDivider, IonLabel, IonGrid, IonRow, IonCol } from '@ionic/react'
import SInput from '../stringInput/StringInput.component'
import MultiSelect from '../multiSelect/MultiSelect.component'
import SingleSelect from '../singleSelect/SingleSelect.component'
import Toggle from '../toggle/Toggle.component'
import DateTimePicker from '../calendar/DateTimePicker/DateTimePicker.component'
import NumberPad from '../numberPad/NumberPad.component'
import CrewInput from '../crewInput/CrewInput.component'
import { COMPONENT_INTERFACE } from './Form.constants'
import { FormElement, FormSection, FormPage, FormValue } from './Form.types'
import {
  propValue,
  toOptions,
  defaultFor,
  handleMultiSelectChange,
  handleSingleSelectChange,
  handleStringInputChange,
  handleToggleChange,
  handleDateTimePickerChange,
  handleNumberPadChange,
} from './Form.handlers'
import { ICustomInputs } from '../../features/fillablePDFs/store/fillablePDFs.types'

export const renderFormElement = (
  element: FormElement,
  currentValue: any,
  options: ReturnType<typeof toOptions>,
  formValue: FormValue,
  onChange: (v: FormValue) => void,
  valueRef?: React.MutableRefObject<FormValue>,
  disabledFields?: ICustomInputs
) => {
  const elementId = element.elementId ?? element.id.toString()
  const commonProps = {
    label: element.label,
    isPHI: !!element.isPHI,
    disabled: !!disabledFields?.find((f) => String(f.questionId) === String(elementId))?.isDisabled,
  }

  switch (element.componentInterface) {
    case COMPONENT_INTERFACE.STRING_INPUT:
      return (
        <SInput
          dataTestID={`SInput_${elementId}`}
          inputValue={currentValue as string}
          onChangeAction={(event) => handleStringInputChange(event, elementId, formValue, onChange)}
          {...commonProps}
        />
      )

    case COMPONENT_INTERFACE.MULTI_SELECT:
      return (
        <MultiSelect
          dataTestID={`MultiSelect_${elementId}`}
          options={options}
          listHeader={element.label}
          savedValues={currentValue}
          action={(selectedItems) => handleMultiSelectChange(selectedItems, elementId, formValue, onChange)}
          {...commonProps}
        />
      )

    case COMPONENT_INTERFACE.SINGLE_SELECT:
      return (
        <SingleSelect
          dataTestID={`SingleSelect_${elementId}`}
          options={options}
          listHeader={element.label}
          selectedOption={currentValue}
          action={(selectedOption) => {
            handleSingleSelectChange(selectedOption, elementId, formValue, onChange)
          }}
          {...commonProps}
        />
      )

    case COMPONENT_INTERFACE.CREW_INPUT:
      return (
        <CrewInput
          dataTestID={`CrewInput_${elementId}`}
          listHeader={element.label}
          selectedOption={currentValue}
          action={(selectedOption) => {
            handleSingleSelectChange(selectedOption, elementId, formValue, onChange)
          }}
          {...commonProps}
        />
      )

    case COMPONENT_INTERFACE.TOGGLE:
      return (
        <Toggle
          dataTestId={`Toggle_${elementId}`}
          rightLabel={element.label}
          isChecked={currentValue as boolean}
          onChangeAction={(isChecked) => handleToggleChange(isChecked, elementId, formValue, onChange)}
        />
      )

    case COMPONENT_INTERFACE.DATE_TIME:
      return (
        <DateTimePicker
          dataTestID={`DateTimePicker_${elementId}`}
          selectedDateValue={currentValue as string | null}
          action={(selectedDate) => handleDateTimePickerChange(selectedDate, elementId, formValue, onChange)}
          {...commonProps}
        />
      )

    case COMPONENT_INTERFACE.NUMBER_PAD:
      return (
        <NumberPad
          dataTestId={`NumberPad_${elementId}`}
          values={currentValue?.toString()?.split('') || []}
          onNumPadClick={(numberValues) => {
            const currentFormValue = valueRef?.current ?? formValue
            handleNumberPadChange(numberValues, elementId, currentFormValue, onChange)
          }}
          {...commonProps}
        />
      )

    case COMPONENT_INTERFACE.INFO:
      return <IonLabel id={`Info_${elementId}`}>{element.label}</IonLabel>

    default:
      return null
  }
}

export const renderSection = (
  section: FormSection,
  formValue: FormValue,
  onChange: (v: FormValue) => void,
  valueRef?: React.MutableRefObject<FormValue>,
  filter?: { level: 'page' | 'section' | 'element'; key: string; value: unknown },
  disabledFields?: ICustomInputs
) => {
  const visibleElements = section.elements?.filter((el) => !propValue(el.properties, 'isHidden') && el.label) || []

  if (visibleElements.length === 0) return null

  return (
    <div key={section.sectionId}>
      {section.header && (
        <IonRow className='ion-padding-horizontal'>
          <IonCol>
            <IonLabel className='text-sm font-medium'>{section.header}</IonLabel>
          </IonCol>
        </IonRow>
      )}

      {visibleElements.map((element) => {
        const elementId = element.elementId ?? element.id.toString()
        const options = toOptions(element.options)
        const currentValue = formValue?.[elementId] ?? defaultFor(element)

        return (
          <React.Fragment key={elementId}>
            <IonRow>
              <IonCol className='picklist' data-testid={`col-${elementId}`}>
                {renderFormElement(element, currentValue, options, formValue, onChange, valueRef, disabledFields)}
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </React.Fragment>
        )
      })}
    </div>
  )
}

export const renderPage = (
  page: FormPage,
  pageIndex: number,
  formValue: FormValue,
  onChange: (v: FormValue) => void,
  valueRef?: React.MutableRefObject<FormValue>,
  filter?: { level: 'page' | 'section' | 'element'; key: string; value: unknown },
  disabledFields?: ICustomInputs
) => {
  return (
    <div key={page.pageId ?? `p-${pageIndex}`}>
      {page.header && (
        <IonItemDivider color='light' className='ion-padding'>
          <IonLabel className='grid-item-divider-label' color='medium'>
            {page.header}
          </IonLabel>
        </IonItemDivider>
      )}

      <IonGrid className='ion-no-padding'>
        {page.sections?.map((section, sectionIndex) => (
          <div key={section.sectionId ?? `s-${sectionIndex}`}>
            {renderSection(section, formValue, onChange, valueRef, filter, disabledFields)}
          </div>
        ))}
      </IonGrid>

      <IonItemDivider color='light' />
    </div>
  )
}
